import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Label } from '@/components/ui/label';
import { useSelector } from 'react-redux';
import type { RootState } from '@/app/store';
import { useAppDispatch } from '@/app/hooks';
import { updateVisualIdentity } from '@/features/workspace/workspaceThunks';
import type {
  WorkspaceVisualIdentity,
  WorkspaceVisualIdentityFontFamily,
  WorkspaceVisualIdentityTheme,
} from '@/features/workspace/types';
import type { WorkspaceVisualIdentityInput } from '@/services/workspace.service';
import { useTranslation } from 'react-i18next';
import { THEME_PRESETS } from './themePresets';

export default function VisualTheme({
  visualIdentity,
}: {
  visualIdentity?: WorkspaceVisualIdentity | null;
}) {
  const { t } = useTranslation('webinars');
  const dispatch = useAppDispatch();
  const currentWorkspace = useSelector((state: RootState) => state.workspace.currentWorkspace);
  const currentTheme = visualIdentity?.theme || 'LIGHT';

  const handleThemeChange = (value: string) => {
    if (!currentWorkspace || (value !== 'LIGHT' && value !== 'DARK')) return;

    // Choisir une base de theme applique la palette complete correspondante,
    // pas seulement le flag clair/sombre, pour un rendu lisible sans retouche.
    const palette = THEME_PRESETS[value];

    const updatedVisualIdentity = {
      fontFamily: visualIdentity?.fontFamily as WorkspaceVisualIdentityFontFamily,
      accentColor: palette.accentColor,
      backgroundColor: palette.backgroundColor,
      secondaryBackgroundColor: palette.secondaryBackgroundColor,
      contrastColor: palette.contrastColor,
      gradient1Color1: visualIdentity?.gradient1Color1,
      gradient1Color2: visualIdentity?.gradient1Color2,
      gradient2Color1: visualIdentity?.gradient2Color1,
      gradient2Color2: visualIdentity?.gradient2Color2,
      theme: value as WorkspaceVisualIdentityTheme,
      defaultIdentity: true,
    } as WorkspaceVisualIdentityInput;

    dispatch(
      updateVisualIdentity({
        workspaceId: currentWorkspace.id,
        input: updatedVisualIdentity,
      }),
    );
  };

  return (
    <RadioGroup
      defaultValue={currentTheme}
      className="flex gap-4"
      onValueChange={handleThemeChange}
    >
      <div className="flex items-center gap-2">
        <RadioGroupItem value="LIGHT" id="r1" />
        <Label htmlFor="r1">{t('visualIdentity.theme.lightLabel')}</Label>
      </div>
      <div className="flex items-center gap-2">
        <RadioGroupItem value="DARK" id="r2" />
        <Label htmlFor="r2">{t('visualIdentity.theme.darkLabel')}</Label>
      </div>
    </RadioGroup>
  );
}
